import type { AppFont, AppLanguage, AppTheme, AppVisualStyle, FrictionSubtype, InterruptionSource, PingType, RescueKind } from './types';

export const APP_VERSION = '0.1.0';
export const APP_BUILD_ID = typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'dev';
export const DB_NAME = 'rethread_db';
export const DB_IDB_KEY = 'sqlite_blob_v1';

export const FRICTION_TYPES: Array<{ type: PingType; label: string }> = [
  { type: 'cant_start', label: "Can't Start" },
  { type: 'stuck_mid_task', label: 'Stuck Mid-Task' },
  { type: 'avoiding', label: 'Avoiding' },
  { type: 'overwhelmed', label: 'Overwhelmed' },
  { type: 'distracted', label: 'Distracted / Scrolling' },
  { type: 'too_wired', label: 'Too Wired' },
  { type: 'too_tired', label: 'Too Tired' },
  { type: 'ruminating', label: 'Ruminating' },
  { type: 'irritable', label: 'Irritable' }
];

export const INTERRUPTION_SOURCES: Array<{ source: InterruptionSource; label: string }> = [
  { source: 'notification', label: 'Notification' },
  { source: 'person', label: 'Person' },
  { source: 'tab_drift', label: 'Tab drift' },
  { source: 'thought_loop', label: 'Thought loop' },
  { source: 'unknown', label: 'Unknown' }
];

type FrictionSubtypeOption = { subtype: FrictionSubtype; label: string };

export const FRICTION_SUBTYPE_OPTIONS_BY_TYPE: Record<PingType, FrictionSubtypeOption[]> = {
  cant_start: [
    { subtype: 'cant_start_too_big', label: 'Too big' },
    { subtype: 'cant_start_unclear', label: 'Unclear next step' },
    { subtype: 'cant_start_perfectionism', label: 'Perfectionism' },
    { subtype: 'cant_start_low_energy', label: 'No energy' }
  ],
  stuck_mid_task: [
    { subtype: 'stuck_mid_task_hard_resume', label: 'Hard to resume' },
    { subtype: 'stuck_mid_task_lost_thread', label: 'Lost the thread' },
    { subtype: 'stuck_mid_task_too_many_tabs', label: 'Too many tabs' },
    { subtype: 'stuck_mid_task_blocked_detail', label: 'Blocked by detail' }
  ],
  avoiding: [
    { subtype: 'avoiding_task_aversion', label: 'Task aversion' },
    { subtype: 'avoiding_fear_of_mistake', label: 'Fear of mistake' },
    { subtype: 'avoiding_bored', label: 'Bored' },
    { subtype: 'avoiding_unclear_next_step', label: 'Unclear next step' }
  ],
  overwhelmed: [
    { subtype: 'overwhelmed_too_many_tasks', label: 'Too many tasks' },
    { subtype: 'overwhelmed_no_priority', label: 'No priority' },
    { subtype: 'overwhelmed_time_pressure', label: 'Time pressure' },
    { subtype: 'overwhelmed_messy_context', label: 'Messy context' }
  ],
  distracted: [
    { subtype: 'distracted_tab_drift', label: 'Tab drift' },
    { subtype: 'distracted_notification', label: 'Notification' },
    { subtype: 'distracted_thought_loop', label: 'Thought loop' },
    { subtype: 'distracted_person_interrupted', label: 'Person interrupted' }
  ],
  too_wired: [
    { subtype: 'too_wired_restless', label: 'Restless' },
    { subtype: 'too_wired_anxious', label: 'Anxious' },
    { subtype: 'too_wired_jittery', label: 'Jittery' },
    { subtype: 'too_wired_cannot_settle', label: "Can't settle" }
  ],
  too_tired: [
    { subtype: 'too_tired_sleepy', label: 'Sleepy' },
    { subtype: 'too_tired_brain_fog', label: 'Brain fog' },
    { subtype: 'too_tired_low_energy', label: 'Low energy' },
    { subtype: 'too_tired_need_break', label: 'Need a break' }
  ],
  ruminating: [
    { subtype: 'ruminating_self_criticism', label: 'Self-criticism' },
    { subtype: 'ruminating_future_worry', label: 'Future worry' },
    { subtype: 'ruminating_social_replay', label: 'Social replay' },
    { subtype: 'ruminating_decision_spiral', label: 'Decision spiral' }
  ],
  irritable: [
    { subtype: 'irritable_overstimulated', label: 'Overstimulated' },
    { subtype: 'irritable_frustrated', label: 'Frustrated' },
    { subtype: 'irritable_interrupted', label: 'Interrupted' },
    { subtype: 'irritable_need_pause', label: 'Need pause' }
  ]
};

const LEGACY_FRICTION_SUBTYPE_LABELS: Record<string, string> = {
  too_big: 'Too big',
  unclear: 'Unclear',
  boring: 'Boring',
  hard_start: 'Hard start',
  hard_resume: 'Hard resume'
};

const FRICTION_SUBTYPE_LABELS: Record<string, string> = {
  ...LEGACY_FRICTION_SUBTYPE_LABELS,
  ...Object.values(FRICTION_SUBTYPE_OPTIONS_BY_TYPE)
    .flat()
    .reduce<Record<string, string>>((acc, option) => {
      acc[option.subtype] = option.label;
      return acc;
    }, {})
};

function humanizeSubtypeKey(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function frictionSubtypeLabel(subtype: FrictionSubtype | null | undefined): string {
  if (!subtype) return 'n/a';
  return FRICTION_SUBTYPE_LABELS[subtype] ?? humanizeSubtypeKey(subtype);
}

export const RESCUE_PREP_ITEMS: Array<{ key: 'phoneSilenced' | 'tabsClosed' | 'docOpen' | 'timerVisible'; label: string }> = [
  { key: 'phoneSilenced', label: 'Phone silenced' },
  { key: 'tabsClosed', label: 'Distracting tabs closed' },
  { key: 'docOpen', label: 'Target doc/tab open' },
  { key: 'timerVisible', label: 'Timer visible' }
];

export const RESCUES: Array<{
  kind: RescueKind;
  label: string;
  durationSec: number;
  prompt?: string;
}> = [
  {
    kind: 'reset_2m',
    label: '2-min reset',
    durationSec: 120
  },
  {
    kind: 'make_smaller_10m',
    label: '10-min make it smaller',
    durationSec: 600,
    prompt: "What's the smallest next step?"
  },
  {
    kind: 'focus_25m',
    label: '25-min focus sprint',
    durationSec: 1500
  },
  {
    kind: 'breathe_90s',
    label: '90-sec breathing reset',
    durationSec: 90
  },
  {
    kind: 'brain_dump_2m',
    label: '2-min brain dump',
    durationSec: 120
  },
  {
    kind: 'walk_5m',
    label: '5-min walk',
    durationSec: 300
  },
  {
    kind: 'stand_water',
    label: 'Stand + water',
    durationSec: 30
  },
  {
    kind: 'one_sentence',
    label: 'Write 1 sentence',
    durationSec: 0
  }
];

export const TOP_PICK_RESCUE_BY_TYPE: Record<PingType, RescueKind> = {
  cant_start: 'make_smaller_10m',
  stuck_mid_task: 'focus_25m',
  avoiding: 'make_smaller_10m',
  overwhelmed: 'brain_dump_2m',
  distracted: 'reset_2m',
  too_wired: 'breathe_90s',
  too_tired: 'stand_water',
  ruminating: 'stand_water',
  irritable: 'walk_5m'
};

export const TOP_PICK_RESCUE_BY_SUBTYPE: Record<FrictionSubtype, RescueKind> = {
  cant_start_too_big: 'make_smaller_10m',
  cant_start_unclear: 'one_sentence',
  cant_start_perfectionism: 'one_sentence',
  cant_start_low_energy: 'stand_water',
  stuck_mid_task_hard_resume: 'make_smaller_10m',
  stuck_mid_task_lost_thread: 'one_sentence',
  stuck_mid_task_too_many_tabs: 'reset_2m',
  stuck_mid_task_blocked_detail: 'one_sentence',
  avoiding_task_aversion: 'walk_5m',
  avoiding_fear_of_mistake: 'breathe_90s',
  avoiding_bored: 'walk_5m',
  avoiding_unclear_next_step: 'one_sentence',
  overwhelmed_too_many_tasks: 'brain_dump_2m',
  overwhelmed_no_priority: 'brain_dump_2m',
  overwhelmed_time_pressure: 'breathe_90s',
  overwhelmed_messy_context: 'reset_2m',
  distracted_tab_drift: 'reset_2m',
  distracted_notification: 'reset_2m',
  distracted_thought_loop: 'breathe_90s',
  distracted_person_interrupted: 'stand_water',
  too_wired_restless: 'walk_5m',
  too_wired_anxious: 'breathe_90s',
  too_wired_jittery: 'breathe_90s',
  too_wired_cannot_settle: 'breathe_90s',
  too_tired_sleepy: 'stand_water',
  too_tired_brain_fog: 'stand_water',
  too_tired_low_energy: 'stand_water',
  too_tired_need_break: 'walk_5m',
  ruminating_self_criticism: 'walk_5m',
  ruminating_future_worry: 'stand_water',
  ruminating_social_replay: 'walk_5m',
  ruminating_decision_spiral: 'brain_dump_2m',
  irritable_overstimulated: 'walk_5m',
  irritable_frustrated: 'walk_5m',
  irritable_interrupted: 'stand_water',
  irritable_need_pause: 'walk_5m',
  too_big: 'make_smaller_10m',
  unclear: 'one_sentence',
  boring: 'walk_5m',
  hard_start: 'make_smaller_10m',
  hard_resume: 'one_sentence'
};

export const TOP_PICK_RESCUE_BY_INTERRUPTION: Record<InterruptionSource, RescueKind> = {
  notification: 'reset_2m',
  person: 'stand_water',
  tab_drift: 'reset_2m',
  thought_loop: 'breathe_90s',
  unknown: 'reset_2m'
};

export function topPickRescueForFriction(
  type: PingType,
  subtype: FrictionSubtype | null | undefined,
  interruptionSource: InterruptionSource | null | undefined
): RescueKind {
  if (subtype && TOP_PICK_RESCUE_BY_SUBTYPE[subtype]) return TOP_PICK_RESCUE_BY_SUBTYPE[subtype];
  if (interruptionSource && TOP_PICK_RESCUE_BY_INTERRUPTION[interruptionSource]) {
    return TOP_PICK_RESCUE_BY_INTERRUPTION[interruptionSource];
  }
  return TOP_PICK_RESCUE_BY_TYPE[type];
}

export const R_LINK_MIN = 5;
export const T_RESOLVE_MIN = 45;
export const BASELINE_WINDOW_MIN = 120;
export const W_LOOKBACK_MIN = 180;

export const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
export const ACTIVE_TIMER_STORAGE_KEY = 'active_timer_v1';
export const FRICTION_START_STORAGE_KEY = 'friction_start_v1';
export const TINY_WIN_DRAFT_STORAGE_KEY = 'tiny_win_draft_v1';
export const PARKING_DRAFT_STORAGE_KEY = 'parking_draft_v1';
export const UX_HINT_LEVEL_STORAGE_KEY = 'ux_hint_level_v1';
export const STUCK_TIMER_HELPER_COUNT_STORAGE_KEY = 'stuck_timer_helper_count_v1';
export const ONBOARDING_DONE_STORAGE_KEY = 'onboarding_done_v1';
export const LEGAL_CONSENT_DONE_STORAGE_KEY = 'legal_consent_done_v1';
export const DATA_DISCLOSURE_CONSENT_STORAGE_KEY = 'data_disclosure_consent_v1';
export const AD_PERSONALIZATION_CONSENT_STORAGE_KEY = 'ad_personalization_consent_v1';
export const APP_SETTINGS_STORAGE_KEY = 'app_settings_v1';
export const RESCUE_SCRIPT_DRAFTS_STORAGE_KEY = 'rescue_script_drafts_v1';
export const FAVORITE_RESCUES_STORAGE_KEY = 'favorite_rescues_v1';

export const APP_THEMES: Array<{ theme: AppTheme; label: string; description: string }> = [
  { theme: 'dracula', label: 'Dracula', description: 'Classic dark purple palette with vivid accents.' },
  { theme: 'monokai', label: 'Monokai', description: 'Dark terminal-inspired palette with bold color cues.' },
  { theme: 'solarized_light', label: 'Solarized Light', description: 'Low-contrast light palette for long sessions.' },
  { theme: 'solarized_dark', label: 'Solarized Dark', description: 'Balanced dark palette tuned for readability.' },
  { theme: 'calm_mint', label: 'Calm Mint', description: 'Soft greens and low visual intensity.' },
  { theme: 'warm_paper', label: 'Warm Paper', description: 'Warm neutrals with softer contrast.' },
  { theme: 'high_contrast', label: 'High Contrast', description: 'Sharper contrast for quick scanning.' },
  { theme: 'sunrise_peach', label: 'Sunrise Peach', description: 'Warm peach tones with gentle contrast.' },
  { theme: 'ocean_breeze', label: 'Ocean Breeze', description: 'Cool blue-greens with calm readability.' },
  { theme: 'slate_focus', label: 'Slate Focus', description: 'Neutral slate palette for low distraction.' }
];

export const APP_VISUAL_STYLES: Array<{ style: AppVisualStyle; label: string; href: string }> = [
  { style: 'soft_tactile', label: 'Soft Tactile', href: '/design-languages/soft-tactile.css' },
  { style: 'paper_panel', label: 'Paper Panel', href: '/design-languages/paper-panel.css' },
  { style: 'tactile_instrument', label: 'Tactile Instrument', href: '/design-languages/tactile-instrument.css' },
  { style: 'comic_board', label: 'Comic Board', href: '/design-languages/comic-board.css' }
];

export const APP_FONTS: Array<{ font: AppFont; label: string; description: string }> = [
  { font: 'atkinson', label: 'Atkinson', description: 'Accessibility-focused default with clear letterforms.' },
  { font: 'system', label: 'System UI', description: 'Use the native platform font stack.' },
  { font: 'lexend', label: 'Lexend', description: 'Wider spacing designed to ease visual tracking.' },
  { font: 'source_sans', label: 'Source Sans', description: 'Neutral sans-serif with calm rhythm.' },
  { font: 'nunito', label: 'Nunito', description: 'Rounded sans-serif with a softer tone.' },
  { font: 'ibm_plex', label: 'IBM Plex Sans', description: 'Structured and technical with strong readability.' }
];

export const APP_LANGUAGES: Array<{ language: AppLanguage; label: string }> = [
  { language: 'en', label: 'English' },
  { language: 'es', label: 'Español' },
  { language: 'zh', label: '中文' },
  { language: 'ja', label: '日本語' }
];
